import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, CardTitle, Button, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input,
  Container, Spinner, Badge
} from 'reactstrap';
import { FaCashRegister, FaPlus, FaEdit, FaTimes, FaStar } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { posActions } from '../../sagas/posSlice';
import { useTranslation } from 'react-i18next';

const POSPage = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(state => state.pos);
  const { t } = useTranslation();
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedPos, setSelectedPos] = useState(null);

  useEffect(() => {
    dispatch(posActions.list());
  }, []);

  /* ================= FORM ================= */
  const formik = useFormik({
    initialValues: {
      code: '',
      name: '',
      address: '',
      active: true,
      phone: '',
      email: ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Nom obligatoire'),
      address: Yup.string().required('Adresse obligatoire'),
      phone: Yup.string().required('Téléphone obligatoire'),
    }),
    onSubmit: (values, { resetForm }) => {
      dispatch(posActions.create({
        data: values,
        onSuccess: () => {
          Swal.fire('Succès', 'Point de vente créé', 'success');
          resetForm();
          setAddModal(false);
        }
      }));
    }
  });

  /* ================= EDIT ================= */
  const handleEditSubmit = (e) => {
    e.preventDefault();

    dispatch(posActions.update({
      id: selectedPos.id,
      data: {
        ...selectedPos,
        name: e.target.name.value,
        address: e.target.address.value,
        phone: e.target.phone.value,
        email: e.target.email.value,
      },
      onSuccess: () => {
        Swal.fire('Succès', 'Point de vente mis à jour', 'success');
        setEditModal(false);
      }
    }));
  };

  /* ================= UI ================= */
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-2 text-muted">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaCashRegister className="me-2 text-primary" />
                Points de vente
              </h4>
              <Button color="primary" onClick={() => setAddModal(true)}>
                <FaPlus className="me-1" /> Nouveau POS
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {list.map(pos => (
          <Col md={4} key={pos.id} className="mb-4">
            <Card className="shadow-sm h-100">
              <CardBody>
                <CardTitle tag="h5">
                  <FaCashRegister className="me-2 text-primary" />
                  {pos.code} — {pos.name}
                </CardTitle>
                <div className="text-muted small">
                  📞 {pos.phone}
                </div>
                <div className="text-muted small">
                  ✉️ {pos.email}
                </div>
                <div className="text-muted mb-3">
                  {pos.address}
                </div>

                <Button
                  color="warning"
                  onClick={() => {
                    setSelectedPos(pos);
                    setEditModal(true);
                  }}
                >
                  <FaEdit className="me-1" /> Modifier
                </Button>
              </CardBody>
            </Card>
          </Col>
        ))}

        {list.length === 0 && (
          <Col className="text-center text-muted">
            Aucun point de vente trouvé
          </Col>
        )}
      </Row>

      {/* ================= ADD MODAL ================= */}
      <Modal isOpen={addModal} toggle={() => setAddModal(false)}>
        <Form onSubmit={formik.handleSubmit}>
          <ModalHeader toggle={() => setAddModal(false)}>
            <FaPlus className="me-2" /> Nouveau point de vente
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Nom *</Label>
              <Input {...formik.getFieldProps('name')} />
            </FormGroup>
            <FormGroup>
              <Label>Téléphone *</Label>
              <Input
                type="tel"
                {...formik.getFieldProps('phone')}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                {...formik.getFieldProps('email')}
              />
            </FormGroup>
            <FormGroup>
              <Label>Adresse *</Label>
              <Input {...formik.getFieldProps('address')} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" type="submit">Ajouter</Button>
            <Button color="secondary" onClick={() => setAddModal(false)}>
              <FaTimes /> Annuler
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* ================= EDIT MODAL ================= */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
        <Form onSubmit={handleEditSubmit}>
          <ModalHeader toggle={() => setEditModal(false)}>
            <FaEdit className="me-2" /> Modifier POS
          </ModalHeader>
          <ModalBody>
            {selectedPos && (
              <>
                <FormGroup>
                  <Label>Nom *</Label>
                  <Input name="name" defaultValue={selectedPos.name} />
                </FormGroup>
                <FormGroup>
                  <Label>Téléphone *</Label>
                  <Input
                    name="phone"
                    defaultValue={selectedPos.phone}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    name="email"
                    defaultValue={selectedPos.email}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Adresse *</Label>
                  <Input name="address" defaultValue={selectedPos.address} />
                </FormGroup>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      name="primary"
                      defaultChecked={selectedPos.primary}
                    />
                    POS principal
                  </Label>
                </FormGroup>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary">Mettre à jour</Button>
            <Button color="secondary" onClick={() => setEditModal(false)}>
              <FaTimes /> Annuler
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Container>
  );
};

export default POSPage;
